import React from 'react';
import { formatDate } from '../../utils/helper';
import { getPlacaApiMismatches } from '../../utils/placaApiCrossCheck';
import './PlacApiResults.css';

const displayValue = (value) => {
    if (value === null || value === undefined || value === '') return 'No registra';
    if (typeof value === 'boolean') return value ? 'Sí' : 'No';
    return String(value);
};

const Field = ({ label, value, alert = false, emphasize = false }) => (
    <div className={`placapi-field ${alert ? 'placapi-field-alert' : ''} ${emphasize ? 'placapi-field-emphasize' : ''}`}>
        <span className="placapi-field-label">{label}</span>
        <span className="placapi-field-value">{displayValue(value)}</span>
    </div>
);

const Category = ({ title, children }) => (
    <section className="placapi-category">
        <h4 className="placapi-category-title">{title}</h4>
        <div className="placapi-fields">{children}</div>
    </section>
);

const BLINDAGE_FIELDS = [
    { key: 'blindado', label: 'Blindado' },
    { key: 'nivelBlindaje', label: 'Nivel de blindaje' },
    { key: 'tipoBlindajeNombre', label: 'Tipo de blindaje' },
    { key: 'autorizacion', label: 'Autorización' },
    { key: 'numeroResolucion', label: 'Número de resolución' },
    { key: 'fechaBlindaje', label: 'Fecha de blindaje' },
    { key: 'fechaDesblindaje', label: 'Fecha de desblindaje' },
    { key: 'fechaExpedicionCertificado', label: 'Fecha certificado' },
];

const formatListItems = (items) => {
    if (!Array.isArray(items) || items.length === 0) return 'Sin registros';
    return items.map((item) => (typeof item === 'string' ? item : JSON.stringify(item))).join(' · ');
};

const asRecords = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'object') return [value];
    return [];
};

const parseRecordDate = (value) => {
    if (!value) return 0;
    const match = String(value).trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (match) {
        return new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1])).getTime();
    }
    const timestamp = Date.parse(value);
    return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getLatestRecord = (items, dateKeys) => {
    const records = asRecords(items);
    if (records.length === 0) return null;
    return records.reduce((latest, current) => {
        const latestTs = Math.max(...dateKeys.map((key) => parseRecordDate(latest[key])));
        const currentTs = Math.max(...dateKeys.map((key) => parseRecordDate(current[key])));
        return currentTs > latestTs ? current : latest;
    });
};

const SOAT_DATE_KEYS = ['fechaVencimiento', 'fechaExpedicion', 'fechaExpediSoat', 'fechaVigencia'];
const TECNO_DATE_KEYS = ['fechaVencimiento', 'fechaExpedicion'];

const RecordList = ({ items, emptyAlert = false, children }) => {
    const records = asRecords(items);
    if (records.length === 0) {
        return <Field label="Registros" value="Sin registros" alert={emptyAlert} />;
    }
    return records.map((item, index) => (
        <div key={item.noCertificado || item.noPoliza || item.noSolicitud || index} className="placapi-list-item">
            {children(item, index)}
        </div>
    ));
};

const sanitizePublicError = (message, fallback = 'Ocurrió un error al obtener los antecedentes.') => {
    if (!message) return fallback;
    return String(message)
        .replace(/consultar PlacApi/gi, 'consultar los antecedentes')
        .replace(/PlacApi/gi, 'el servicio externo')
        .trim();
};

const TIMEOUT_SOURCE_MESSAGE = 'La fuente oficial tardó demasiado en responder y se agotó el tiempo de espera.';
const AUTH_OR_CREDIT_STATUSES = [401, 402];

const PlacApiResults = ({
    placaApi,
    vehicleData,
    onRetry,
    onConsultByVin,
    showRetry = false,
    showConsultedAt = false,
    showTechnicalError = false,
}) => {
    if (placaApi?.error && !placaApi?.data) {
        const consultBy = placaApi.error.consultBy || 'document';
        const statusCode = Number(placaApi.error.statusCode);
        const isTimeout = placaApi.error.code === 'timeout' || statusCode === 504;
        const friendlyMessage = sanitizePublicError(placaApi.error.message);
        const sourceMessage = sanitizePublicError(placaApi.error.sourceMessage, '')
            || (isTimeout ? TIMEOUT_SOURCE_MESSAGE : '');
        const sourceComplement = sourceMessage && sourceMessage !== friendlyMessage
            ? sourceMessage
            : '';
        const canOfferVin = consultBy !== 'vin'
            && !AUTH_OR_CREDIT_STATUSES.includes(statusCode)
            && typeof onConsultByVin === 'function';
        const retryActions = showRetry && typeof onRetry === 'function' && (
            <div className="placapi-error-actions">
                <button type="button" className="placapi-retry-button" onClick={onRetry}>
                    Reintentar consulta
                </button>
                {canOfferVin && (
                    <button type="button" className="placapi-retry-button placapi-vin-button" onClick={onConsultByVin}>
                        Consultar por VIN
                    </button>
                )}
            </div>
        );

        if (!showTechnicalError) {
            return (
                <div className="placapi-panel">
                    <div className="placapi-notice" role="status">
                        <p>
                            <strong>No se pudieron obtener los datos en esta consulta.</strong>
                        </p>
                        <p>Puedes continuar y registrar los antecedentes de forma manual.</p>
                        {showRetry && canOfferVin && (
                            <p>Puedes consultar por VIN si no tienes el documento del propietario.</p>
                        )}
                        {retryActions}
                    </div>
                </div>
            );
        }

        return (
            <div className="placapi-panel">
                <div className="placapi-error" role="alert">
                    <p>
                        <strong>No se pudo obtener la consulta de antecedentes.</strong>
                    </p>
                    <p>{friendlyMessage}</p>
                    {sourceComplement && (
                        <div className="placapi-error-source">
                            <p className="placapi-error-source-label">
                                El siguiente detalle lo reportó el servicio externo (no es un error de IVISNK9):
                            </p>
                            <p className="placapi-error-source-text">{sourceComplement}</p>
                        </div>
                    )}
                    {showRetry && canOfferVin && (
                        <p>Puedes consultar por VIN si no tienes el documento del propietario.</p>
                    )}
                    {retryActions}
                </div>
            </div>
        );
    }

    const data = placaApi?.data;
    if (!data) return null;

    const general = data.informacionGeneral || {};
    const technical = data.datosTecnicos || {};
    const mismatches = getPlacaApiMismatches(vehicleData, data);
    const latestSoat = getLatestRecord(data.soat, SOAT_DATE_KEYS);
    const latestTecno = getLatestRecord(data.tecnoMecanica, TECNO_DATE_KEYS);

    return (
        <div className="placapi-panel">
            {showConsultedAt && placaApi.fetchedAt && (
                <p className="placapi-consulted-at">
                    <strong>Fecha de consulta:</strong> {formatDate(placaApi.fetchedAt)}
                </p>
            )}

            {mismatches.length > 0 && (
                <div className="placapi-mismatch" role="alert">
                    <p>
                        <strong>Inconsistencias entre los datos registrados y la consulta de antecedentes</strong>
                    </p>
                    <div className="placapi-mismatch-table-wrap">
                        <table className="placapi-mismatch-table">
                            <thead>
                                <tr>
                                    <th>Campo</th>
                                    <th>Datos registrados</th>
                                    <th>Consulta de antecedentes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {mismatches.map((item) => (
                                    <tr key={item.label}>
                                        <td>{item.label}</td>
                                        <td>{item.formValue}</td>
                                        <td>{item.placapiValue}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <Category title="Identificación principal">
                <Field label="Placa" value={data.plate || general.noPlaca} alert />
                <Field label="VIN" value={data.vin || general.noVin} />
                <Field label="Documento del propietario" value={data.documentNumber} />
            </Category>

            <Category title="Información general">
                <Field label="Marca" value={general.marca} />
                <Field label="Línea" value={general.linea} />
                <Field label="Modelo" value={general.modelo} />
                <Field label="Color" value={general.color} />
                <Field label="Clase de vehículo" value={general.claseVehiculo} />
                <Field label="Clasificación" value={general.clasificacion} />
                <Field label="Cilindraje" value={general.cilindraje} />
                <Field label="Tipo de carrocería" value={general.tipoCarroceria} />
                <Field label="Tipo de combustible" value={general.tipoCombustible} />
                <Field label="Tipo de servicio" value={general.tipoServicio} />
                <Field label="Estado del vehículo" value={general.estadoDelVehiculo ?? general.estadoVehiculo} alert emphasize />
                <Field label="Fecha de matrícula" value={general.fechaMatricula} alert />
                <Field label="Días matriculado" value={general.diasMatriculado} alert />
                <Field label="Organismo de tránsito" value={general.organismoTransito} alert />
                <Field label="Licencia de tránsito" value={general.noLicenciaTransito} />
                <Field label="Número de motor" value={general.noMotor} />
                <Field label="Número de chasis" value={general.noChasis} />
                <Field label="Puertas" value={general.puertas} />
                <Field label="Motor regrabado" value={general.esRegrabadoMotor} alert />
                <Field label="Chasis regrabado" value={general.esRegrabadoChasis} alert />
                <Field label="Prendas" value={general.prendas ?? general.tienePrendas} alert />
                <Field label="Gravámenes" value={general.tieneGravamenes} alert />
                <Field label="Repotenciado" value={general.repotenciado} alert />
                <Field label="Vehículo de enseñanza" value={general.vehiculoEnsenanza} />
            </Category>

            <Category title="Datos técnicos">
                <Field label="Pasajeros sentados" value={technical.pasajerosSentados} />
                <Field label="Número de ejes" value={technical.noEjes} />
            </Category>

            <Category title="SOAT">
                {!latestSoat ? (
                    <Field label="Registros" value="Sin registros" alert />
                ) : (
                    <>
                        <Field label="Estado" value={latestSoat.estado} alert />
                        <Field label="Entidad que expide" value={latestSoat.entidadExpideSoat} />
                    </>
                )}
            </Category>

            <Category title="Tecnomecánica">
                {!latestTecno ? (
                    <Field label="Registros" value="Sin registros" alert />
                ) : (
                    <>
                        <Field label="Vigente" value={latestTecno.vigente} alert />
                        <Field label="Estado" value={latestTecno.estado} alert />
                    </>
                )}
            </Category>

            <Category title="Historial de solicitudes">
                {(data.solicitudes || []).length === 0 ? (
                    <Field label="Registros" value="Sin registros" />
                ) : (
                    data.solicitudes.map((item) => (
                        <div key={item.noSolicitud} className="placapi-list-item">
                            <Field label="No. solicitud" value={item.noSolicitud} alert />
                            <Field label="Fecha" value={item.fechaSolicitud} />
                            <Field label="Trámite" value={item.tramitesRealizados} />
                            <Field label="Entidad" value={item.entidad} />
                            <Field label="Estado" value={item.estado} alert />
                        </div>
                    ))
                )}
            </Category>

            <Category title="Certificado DIJIN">
                <RecordList items={data.certificadoDijin}>
                    {(item) => (
                        <>
                            <Field label="No. certificado" value={item.noCertificado} />
                            <Field label="Fecha de expedición" value={item.fechaExpedicion} />
                            <Field label="Entidad" value={item.entidadCertificado} />
                            <Field label="Estado" value={item.estado ?? item.estadoCertificado} alert />
                        </>
                    )}
                </RecordList>
            </Category>

            <Category title="Desintegración">
                <Field label="Desintegrar" value={data.desintegracion?.desintegrar} />
                <RecordList items={data.certificadoDesintegracion}>
                    {(item) => (
                        <>
                            <Field label="No. certificado" value={item.noCertificado} />
                            <Field label="Estado del certificado" value={item.estadoCertificado} />
                            <Field label="Fecha de expedición" value={item.fechaExpedicion} />
                        </>
                    )}
                </RecordList>
            </Category>

            <Category title="Repotenciación">
                <Field label="Fecha de repotenciación" value={data.informacionRepotenciado?.fechaRepotenciacion} />
                <Field label="Modelo repotenciado" value={data.informacionRepotenciado?.modeloRepotenciado} />
            </Category>

            <Category title="Pólizas / cauciones">
                <RecordList items={data.polizaCaucion}>
                    {(item) => (
                        <>
                            <Field label="No. póliza" value={item.noPoliza} />
                            <Field label="Estado de la póliza" value={item.estadoPoliza} />
                            <Field label="Fecha de expedición" value={item.fechaExpedicion} />
                            <Field label="Fecha de vigencia" value={item.fechaVigenciaPoliza} />
                            <Field label="Estado del certificado" value={item.estadoCertificado} alert />
                        </>
                    )}
                </RecordList>
            </Category>

            <Category title="Registro inicial">
                <RecordList items={data.registroInicial} emptyAlert>
                    {(item) => (
                        <>
                            <Field label="No. certificado" value={item.noCertificado} alert />
                            <Field label="Fecha de expedición" value={item.fechaExpedicion} alert />
                            <Field label="Estado del certificado" value={item.estadoCertificado} alert />
                            <Field label="Placa de reposición" value={item.placaReposicion} alert />
                        </>
                    )}
                </RecordList>
                <RecordList items={data.registroInicialInvc}>
                    {(item) => (
                        <>
                            <Field label="No. certificado INVC" value={item.noCertificado} />
                            <Field label="Fecha de expedición INVC" value={item.fechaExpedicion} />
                            <Field label="Estado certificado INVC" value={item.estadoCertificado} />
                        </>
                    )}
                </RecordList>
            </Category>

            <Category title="Blindaje">
                {BLINDAGE_FIELDS.map((field) => (
                    <Field
                        key={field.key}
                        label={field.label}
                        value={data.informacionBlindaje?.[field.key]}
                        alert
                    />
                ))}
            </Category>

            <Category title="Garantías / saneamiento">
                <Field label="Limitación a la propiedad" value={formatListItems(data.limitacionPropiedad)} alert />
                <Field label="Garantías mobiliarias" value={formatListItems(data.garantiasMobiliarias)} alert />
                <Field label="Prendas / garantía mobiliaria" value={formatListItems(asRecords(data.prendasGarantiaMobiliaria))} alert />
            </Category>
        </div>
    );
};

export default PlacApiResults;
